import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// PrimeNG modules
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { PasswordModule } from 'primeng/password';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TextareaModule } from 'primeng/textarea';
import { SliderModule } from 'primeng/slider';
import { ColorPickerModule } from 'primeng/colorpicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputMaskModule } from 'primeng/inputmask';
import { InputOtpModule } from 'primeng/inputotp';
import { KnobModule } from 'primeng/knob';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { RatingModule } from 'primeng/rating';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SelectModule } from 'primeng/select';
import { EditorModule } from 'primeng/editor';
import { TreeModule } from 'primeng/tree';
import { TreeSelectModule } from 'primeng/treeselect';
import { TabsModule } from 'primeng/tabs';
import { PaginatorModule } from 'primeng/paginator';
import { TagModule } from 'primeng/tag';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { AccordionModule } from 'primeng/accordion';
import { TooltipModule } from 'primeng/tooltip';

// Reusable components
import { PrimengButtonComponent } from './components/primeng-button/primeng-button.component';
import { PrimengCardComponent } from './components/primeng-card/primeng-card.component';
import { PrimengTableComponent } from './components/primeng-table/primeng-table.component';
import { PrimengDialogComponent } from './components/primeng-dialog/primeng-dialog.component';
import { DynamicFieldComponent } from './components/dynamic-field/dynamic-field.component';
import { PrimengDrawerComponent } from './components/primeng-drawer/primeng-drawer.component';
import { ConfirmDialogComponent } from './components/primeng-confirm-dialog/primeng-confirm-dialog.component';
import { PrimengToastComponent } from './components/primeng-toast/primeng-toast.component';
import { PrimengSpinnerComponent } from './components/primeng-spinner/primeng-spinner.component';
import { ChartComponent } from './components/chart/chart.component';
import { PrimengSelectComponent } from './components/primeng-select/primeng-select.component';
import { PrimengMultiSelectComponent } from './components/primeng-multiselect/primeng-multiselect.component';
import { PrimengInputTextComponent } from './components/primeng-input-text/primeng-input-text.component';
import { PrimengInputNumberComponent } from './components/primeng-input-number/primeng-input-number.component';
import { PrimengDatepickerComponent } from './components/primeng-datepicker/primeng-datepicker.component';
import { PrimengAutoCompleteComponent } from './components/primeng-autocomplete/primeng-autocomplete.component';
import { PrimengToggleSwitchComponent } from './components/primeng-toggle-switch/primeng-toggle-switch.component';
import { PrimengCheckboxComponent } from './components/primeng-checkbox/primeng-checkbox.component';
import { PrimengColorPickerComponent } from './components/primeng-colorpicker/primeng-colorpicker.component';
import { PrimengTabsComponent } from './components/primeng-tabs/primeng-tabs.component';
import { PrimengPaginatorComponent } from './components/primeng-paginator/primeng-paginator.component';
import { PrimengTagComponent } from './components/primeng-tag/primeng-tag.component';
import { ModuleAccessTreeComponent } from './components/module-access-tree/module-access-tree.component';
import { EchartComponent } from './components/echart/echart.component';
import { PrimengAccordionComponent } from './components/primeng-accordion/primeng-accordion.component';
import { MockDataNoticeComponent } from './components/mock-data-notice/mock-data-notice.component';
import { InteractiveLoaderComponent } from './components/interactive-loader/interactive-loader.component';
import { ScopeHierarchyTreeComponent } from './components/scope-hierarchy-tree/scope-hierarchy-tree.component';
import { SafeLabelPipe } from './pipes/safe-label.pipe';
import { HasPermissionDirective } from './directives/has-permission.directive';

// PrimeNG Services and more modules
import { DrawerModule } from 'primeng/drawer';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BaseIcon } from "primeng/icons/baseicon";

const primeNgModules = [
  ButtonModule,
  CardModule,
  TableModule,
  InputTextModule,
  CheckboxModule,
  DialogModule,
  DatePickerModule,
  InputGroupModule,
  InputGroupAddonModule,
  AutoCompleteModule,
  PasswordModule,
  RadioButtonModule,
  TextareaModule,
  SliderModule,
  ColorPickerModule,
  InputNumberModule,
  InputMaskModule,
  InputOtpModule,
  KnobModule,
  MultiSelectModule,
  ToggleButtonModule,
  ToggleSwitchModule,
  RatingModule,
  SelectButtonModule,
  SelectModule,
  EditorModule,
  TreeModule,
  TreeSelectModule,
  DrawerModule,
  ConfirmDialogModule,
  ToastModule,
  ProgressSpinnerModule,
  TabsModule,
  PaginatorModule,
  TagModule,
  BreadcrumbModule,
  AccordionModule,
  TooltipModule,
];

const reusableComponents = [
  PrimengButtonComponent,
  PrimengCardComponent,
  PrimengTableComponent,
  PrimengDialogComponent,
  DynamicFieldComponent,
  PrimengDrawerComponent,
  ConfirmDialogComponent,
  PrimengToastComponent,
  PrimengSpinnerComponent,
  ChartComponent,
  PrimengSelectComponent,
  PrimengMultiSelectComponent,
  PrimengInputTextComponent,
  PrimengInputNumberComponent,
  PrimengDatepickerComponent,
  PrimengAutoCompleteComponent,
  PrimengToggleSwitchComponent,
  PrimengCheckboxComponent,
  PrimengColorPickerComponent,
  PrimengTabsComponent,
  PrimengPaginatorComponent,
  PrimengTagComponent,
  ModuleAccessTreeComponent,
  EchartComponent,
  PrimengAccordionComponent,
  MockDataNoticeComponent,
  InteractiveLoaderComponent,
  ScopeHierarchyTreeComponent,
];

@NgModule({
  declarations: [...reusableComponents, SafeLabelPipe, HasPermissionDirective],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ...primeNgModules,
    BaseIcon,
  ],
  exports: [
    ...primeNgModules,
    ...reusableComponents,
    SafeLabelPipe,
    HasPermissionDirective,
  ],
  providers: [
    ConfirmationService,
    MessageService,
  ]
})
export class SharedModule {}
